import {api} from "@/shared/api";

import {BasicDataTable, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {isProjectTimeDateTimeChangeAllowed} from "@/shared/securityHelper";
import {ProjectTimeBulkSelector} from "./projectTimeBulkSelector";
import {showProjectTimeBulkResult} from "@/shared/bulkOperationHelper";
import {RecordingTable} from "@/shared/recording/recordingTable";
import {htmlEscape} from "@/shared/utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDateTime, formatDuration, parseISOZonedDateTime} from "@/shared/utils/datefunctions";
import {ProjectTimeBulkOperationResult, ReportProjectTime} from "@/shared/timr";
import {RecordedEntryStatus} from "@/shared/timrTypes";

export class ProjectTimeTable extends RecordingTable<ReportProjectTime, ProjectTimeBulkSelector> {
	public static readonly RECORDING_PROJECT_TIMES_URL = "/timr/recording/getProjectTimes.json";
	public static readonly REPORT_PROJECT_TIMES_URL = "/timr/reports/getProjectTimes.json";

	public static readonly EVENT_SHOW_TASK_DETAILS = "showTaskDetails";

	/**
	 * If new columns are added, also modify {@link #buildSubTableColumns} to prevent visual errors in the subtable.
	 */
	protected getColumnSettings(): TypedColumnSettings<ReportProjectTime>[] {
		return [
			{
				data: "id",
				orderable: false,
				createdCell: this.renderRowBorder,
				render: BasicDataTable.renderSelectCheckbox,
				visible: this.config.allowSelect,
				width: BasicDataTable.COLUMN_SIZE_CHECKBOX
			},
			{
				data: "id",
				orderable: false,
				createdCell: (this.config.allowSelect ? null : this.renderRowBorder),
				className: "context-menu",
				render: this.contextMenu.render,
				width: BasicDataTable.COLUMN_SIZE_MENU
			},
			{
				data: "userName",
				orderable: this.config.orderable,
				render: BasicDataTable.renderText,
				visible: this.config.showUser
			},
			{
				data: "taskBreadCrumbs",
				orderable: this.config.orderable,
				className: "table-data-task",
				render: BasicDataTable.renderProjectTimeTaskPath
			},
			{
				data: "startPosition",
				orderable: false,
				render: BasicDataTable.renderPositionIcon,
				className: "gps-position gps-position-start no-padding",
				width: BasicDataTable.COLUMN_SIZE_GPS_ICON
			},
			{
				data: "start",
				orderable: this.config.orderable,
				render: BasicDataTable.renderDateTimeWithTimeZone,
				className: "pl-10 pr-10 time-column"
			},
			{
				data: "breakTime",
				orderable: false,
				render: RecordingTable.renderBreakTime,
				className: "text-right break-time-popover-anchor",
				width: BasicDataTable.COLUMN_SIZE_BREAKTIME
			},
			{
				data: "durationNet",
				orderable: false,
				render: RecordingTable.renderDuration,
				className: "text-right",
				width: BasicDataTable.COLUMN_SIZE_DURATION
			},
			{
				data: "attachments",
				orderable: false,
				render: ProjectTimeTable.renderAttachments,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON,
				visible: this.config.projectTimeAttachmentsEnabled
			},
			{
				data: "description",
				orderable: this.config.orderable,
				className: "table-data-description",
				render: BasicDataTable.renderDescriptionText
			},
			{
				data: "status",
				orderable: false,
				render: this.renderQuickSwitch,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_QUICK_SWITCH_BUTTON,
				visible: this.config.allowQuickSwitch
			},
			{
				data: "validations", orderable: false, render: (d, t, r, m) => this.renderValidationWarning(d, t, r, m),
				className: "slim-icon-column validations", width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				data: "billable",
				orderable: false,
				render: ProjectTimeTable.renderBillable,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				data: "status",
				orderable: false,
				render: RecordingTable.renderStatus,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[5, "desc"]];
	}

	protected delete(recording: ReportProjectTime) {
		super.delete(recording);

		if (this.recordingDeletable(recording)) {
			BootstrapModal.confirm({
				title: "projectTime.delete.title",
				message: messages.withArguments(
					"projectTime.delete.message",
					recording.taskName,
					recording.description ? " " + messages.withArguments("record.delete.message.description", recording.description) : "",
					formatDateTime(parseISOZonedDateTime(recording.start))
				),
				yesLabel: "confirmation.delete",
				noLabel: "confirmation.cancel",
				yesAction: (modal: BootstrapModal.Modal, button?: JQuery) => {
					api("recording/deleteProjectTime.json")
						.post({
							id: recording.id
						})
						.done(() => {
							this.reload(true);
							// DEVNOTE: Global Event for project time status panel to update the taskDropdown accordingly
							$(document).triggerHandler(ProjectTimeTable.EVENT_DELETE_RECORDING);
						})
						.always(() => {
							modal.close();
						});
				}
			});
		}
	}

	protected renderQuickSwitch(this: void, data: RecordedEntryStatus, type: string, recording: ReportProjectTime, meta: unknown): string {
		return super.renderQuickSwitch(data, type, recording, meta, recording.taskName);
	}

	protected renderValidationWarning(_data: unknown, _type: string, recording: ReportProjectTime, _meta: unknown): string {
		if (globals.savedValidationsEnabled) {
			return this.renderSeverityIcon(recording.validations?.relevant ?? []);
		} else {
			return null;
		}
	}

	protected showRevisions(recording: ReportProjectTime, tr: JQuery) {
		super.showRevisions(recording, tr);

		if (!this.dataTable.row(tr).child.isShown()) {
			api("reports/getProjectTimeRevisions.json")
				.get(this.extendAjaxData({id: recording.id}, this.getRoundingFilterParams()))
				.done(projectTimeRevisions => {
					this.renderSubTable(projectTimeRevisions.data, tr, ProjectTimeTable.ROW_CLASS_REVISION, (recording) => this.buildRevisionRow(recording));
				});
		} else {
			this.renderSubTable(null, tr, ProjectTimeTable.ROW_CLASS_REVISION, (recording) => this.buildRevisionRow(recording));
		}
	}

	protected showConflicts(recording: ReportProjectTime, tr: JQuery) {
		super.showConflicts(recording, tr);

		if (!this.dataTable.row(tr).child.isShown()) {
			api("reports/getConflictedProjectTimes.json")
				.get(this.extendAjaxData({id: recording.id}, this.getRoundingFilterParams()))
				.done((data) => {
					this.renderSubTable(data.conflictedProjectTimes, tr, ProjectTimeTable.ROW_CLASS_CONFLICT, (recording) => this.buildConflictsRow(recording));
				});
		} else {
			this.renderSubTable(null, tr, ProjectTimeTable.ROW_CLASS_CONFLICT, (recording) => this.buildConflictsRow(recording));
		}
	}

	protected markConflictAsResolved(recording: ReportProjectTime) {
		super.markConflictAsResolved(recording);

		api("reports/markProjectTimeConflictAsResolved.json")
			.post({
				id: recording.id
			})
			.done(() => {
				this.reload();
			});
	}

	protected setStatus(recording: ReportProjectTime, statusNumber: number) {
		super.setStatus(recording, statusNumber);

		api("reports/updateProjectTimeStatus.json")
			.post({
				statusNumber: statusNumber,
				idsInclude: recording.id,
				allSelected: false
			})
			.done((result: ProjectTimeBulkOperationResult) => {
				if (!result.allUpdated) {
					showProjectTimeBulkResult(result, "report.setStatus.");
				}
				this.reload();
			});
	}

	protected renderRowBorder(this: void, td: unknown, _type: unknown, recording: ReportProjectTime, _meta: unknown): void {
		if (recording.billable) {
			$(td).addClass("border-row-BILLABLE");
		} else {
			$(td).addClass("border-row-NOT_BILLABLE");
		}
	}

	public static renderBillable(this: void, data: boolean, _type: string, _recording: ReportProjectTime, _meta: unknown): string {
		return data ? `<i class="fal fa-dollar-sign" title="${messages["reports.filter.billable"]}"></i>` : "";
	}

	public static renderAttachments(this: void, data: number, _type: string, _recording: ReportProjectTime, _meta: unknown): string {
		if (data > 0) {
			return `<i class="fal fa-image" title="${messages["attachments.label"]}"></i>`;
		} else {
			return "";
		}
	}

	protected renderPopoverContent(recording: ReportProjectTime, rowClass?: string): string {
		let content = "";
		const customTaskFields = this.renderCustomTaskFieldPopoverContent(recording);
		const superContent: string = super.renderPopoverContent(recording, rowClass);
		const taskDescription = recording.taskDescription ? htmlEscape(recording.taskDescription).replace(/\n/g, "<br/>") : null;

		if (taskDescription) {
			content += `<div class="text-left text-uppercase text-size-small text-grey">${messages["record.taskDescription.message"]}</div>`;
			content += taskDescription;
		}
		if (content && (superContent || customTaskFields)) {
			content += `<div class="popover-content-divider"></div>`;
		}
		if (customTaskFields) {
			content += `<div class="text-left text-uppercase text-size-small text-grey">${messages["record.customTaskFields.message"]}</div>`;
			content += customTaskFields;
		}
		if (customTaskFields && superContent) {
			content += `<div class="popover-content-divider"></div>`;
		}
		content += superContent;
		return content;
	}

	protected renderCustomTaskFieldPopoverContent(recording: ReportProjectTime) {
		let customTaskFields = "";
		if (globals.customTaskField1 && recording.customTaskField1) {
			customTaskFields += this.renderPopoverContentData(globals.customTaskField1, recording.customTaskField1, false);
		}
		if (globals.customTaskField2 && recording.customTaskField2) {
			customTaskFields += this.renderPopoverContentData(globals.customTaskField2, recording.customTaskField2, false);
		}
		if (globals.customTaskField3 && recording.customTaskField3) {
			customTaskFields += this.renderPopoverContentData(globals.customTaskField3, recording.customTaskField3, false);
		}
		return customTaskFields;
	}

	protected buildSubTableColumns(recording: ReportProjectTime): JQuery[] {
		return [
			this.config.showUser ? this.buildTextColumn(recording.userName) : null,
			this.buildHtmlColumn(BasicDataTable.renderProjectTimeTaskPath(recording.taskBreadCrumbs, null, recording, null), "table-data-task"),
			this.buildPositionColumn(recording),
			this.buildDateTimeColumn(recording, recording.start),
			this.buildBreakTimeColumn(recording, recording.breakTime),
			this.buildDurationColumn(recording, recording.durationNet),
			SecurityStatus.isProjectTimeAttachmentsAllowed() ? this.buildIconColumn(ProjectTimeTable.renderAttachments(recording.attachments, null, recording, null)) : null,
			this.buildHtmlColumn(BasicDataTable.renderDescriptionText(recording.description, null, recording, null), "table-data-description"),
			this.config.allowQuickSwitch ? this.buildEmptyColumn() : null,
			this.buildEmptyColumn(), /* we don't display the validations warning icon in revision or conflicted rows */
			this.buildIconColumn(ProjectTimeTable.renderBillable(recording.billable, null, recording, null)),
			this.buildIconColumn(RecordingTable.renderStatus(recording.status, null, recording, null))
		];
	}

	public createBulkSelector(): ProjectTimeBulkSelector {
		return new ProjectTimeBulkSelector({
			dataTable: this.dataTable
		});
	}

	protected isDateTimeChangeAllowed(anyUser: boolean, userId: number, allowedUserIds: number []): boolean {
		return isProjectTimeDateTimeChangeAllowed(anyUser, userId, allowedUserIds);
	}

	protected updateSelectedRowsCount() {
		const selectedRowsSums = this.bulkSelector.getSums();
		const selectedRowsDurationBillableSum = formatDuration(selectedRowsSums.durationBillableSum);
		const selectedRowsDurationNotBillableSum = formatDuration(selectedRowsSums.durationNotBillableSum);

		super.updateSelectedRowsCount(" " + messages.withArguments("bulkselection.projectTime.duration",
			selectedRowsDurationBillableSum,
			selectedRowsDurationNotBillableSum)
		);
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addAfterLabel("report.delete", menu => {
				menu.addDivider()
					.addMenuEntry({
						label: "report.showTaskDetails",
						action: (recording) => { $(this).triggerHandler(ProjectTimeTable.EVENT_SHOW_TASK_DETAILS, [recording]); }
					});
			});
	}

	protected recordingResumable(recording: ReportProjectTime): boolean {
		return super.recordingResumable(recording)
			&& this.isTaskNotLockedForRecording(recording);
	}

	public recordingChangeable(recording: ReportProjectTime): boolean {
		return super.recordingChangeable(recording)
			&& this.isTaskNotLockedForRecording(recording);
	}

	protected recordingDeletable(recording: ReportProjectTime): boolean {
		return super.recordingDeletable(recording)
			&& this.isTaskNotLockedForRecording(recording);
	}

	protected showMarkConflictAsResolved(recording: ReportProjectTime): boolean {
		return super.showMarkConflictAsResolved(recording)
			&& this.isTaskNotLockedForRecording(recording);
	}

	private isTaskNotLockedForRecording(recording: ReportProjectTime): boolean {
		return !recording.isTaskLocked;
	}

	protected isStatusNumberEnabled(status: RecordedEntryStatus, recording: ReportProjectTime): boolean {
		return super.isStatusNumberEnabled(status, recording) && (status > recording.status || this.isTaskNotLockedForRecording(recording));
	}

	protected getRecordingStatus(recording: ReportProjectTime): RecordedEntryStatus {
		return recording.status;
	}

	protected isConflicted(recording: ReportProjectTime): boolean {
		return recording.conflicted;
	}
}
